<template>
  <!-- Show suspended screen if system is suspended -->
  <SuspendedScreen
    v-if="isSystemSuspended"
    :currentLanguage="$store.state.language || 'de'"
  />

  <div v-else id="order-display-screen" class="w-full h-full flex flex-col">
      <!-- Audio Blocked Indicator -->
      <div
        v-if="soundEnabled && !audioUnlocked"
        class="audio-blocked-indicator"
        @click="unlockAudioManually"
      >
        <i class="fas fa-volume-mute"></i>
        <span>{{ language === 'ar' ? 'اضغط لتفعيل الصوت' : 'Tippen für Sound' }}</span>
      </div>

      <!-- Offline Warning Modal -->
      <OfflineWarningModal />
      <!-- Top Edge Touch Zone - Only active when header is hidden -->
      <div 
        v-if="!isHeaderVisible"
        class="top-touch-zone"
        @click="showHeader"
        @touchstart="showHeader"
        @touchend="showHeader"
        @mousedown="showHeader"
      ></div>
      
      <!-- Collapsible Header -->
      <transition name="header-slide">
        <div v-if="isHeaderVisible" class="header-container enhanced-header" @mouseenter="resetAutoHideTimer" @mouseleave="startAutoHideTimer">
          <!-- Original AppHeader -->
          <div class="clean-app-header">
            <AppHeader
              @showArchive="showArchiveModal"
              @openSettings="showSettingsModal = true"
              @showMealWaveConnection="showMealWaveConnectionModal = true"
              :mealwaveConnectionStatus="mealwaveConnectionStatus"
            />
          </div>
          
          <!-- Admin Message Container -->
          <div v-if="adminMessage.text && !adminMessage.dismissed" 
               id="admin-message-container" 
               class="mx-4 mt-2 p-4 ods-admin-msg flex justify-between items-center">
            <p id="admin-message-text" class="font-bold">{{ adminMessage.text }}</p>
            <button @click="dismissMessage" 
                    id="dismiss-message" 
                    class="ods-btn">
              إغلاق
            </button>
          </div>
        </div>
      </transition>
      
      <!-- Two-column layout container -->
      <div class="flex-1 flex flex-col md:flex-row relative" :class="{ 'full-height': !isHeaderVisible }">
        <!-- Divider Line -->
        <div class="section-divider"></div>
        <!-- Left Column: New Orders -->
        <div class="flex-1 flex flex-col ods-column">
          <div class="ods-col-head ods-col-head--new sticky top-0 z-10">
            <i class="fas fa-bell bell-ring"></i>
            <h2>{{ $t('newOrders') }}</h2>
            <div class="count-badge">{{ newOrdersCount }}</div>
          </div>
          <div id="new-orders-container" class="flex-1 p-4 overflow-y-auto grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); grid-auto-rows: max-content; max-height: calc(100vh - 55px);">
            <div v-if="newOrders.length === 0" class="text-center py-10 font-bold w-full ods-empty">
              <p>{{ $t('noNewOrders') }}</p>
            </div>
            <template v-else>
              <OrderCard
                v-for="order in filteredNewOrders"
                :key="order.id"
                :ref="`newOrderCard_${order.id}`"
                :order="order"
                :isNew="true"
                :isVirtual="order.isVirtualOrder"
                :familyColor="order._familyColor || (order.parentOrderId ? getOrderFamilyColor(order.parentOrderId) : null)"
                :class="{
                  'birth-enter': order.birthAnimation,
                  'birth-next-to-mom': order.birthNextToMom
                }"
                @action="handleOrderAction"
                @viewDetails="showOrderDetails"
                @familyCreated="updateOrderFamilyStatus"
                @criticalAlert="handleCriticalAlert"
              />
            </template>
          </div>
        </div>

        <!-- Right Column: In Progress Orders -->
        <div class="flex-1 flex flex-col ods-column">
          <div class="ods-col-head ods-col-head--prep sticky top-0 z-10">
            <i class="fas fa-fire fire-flicker"></i>
            <h2>{{ $t('preparingOrders') }}</h2>
            <div class="count-badge">{{ preparingOrdersCount }}</div>
          </div>
          <div id="preparing-orders-container" class="flex-1 p-4 overflow-y-auto grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); grid-auto-rows: max-content; max-height: calc(100vh - 55px);">
            <div v-if="preparingOrders.length === 0" class="text-center py-10 font-bold w-full ods-empty">
              <p>{{ $t('noPreparingOrders') }}</p>
            </div>
            <template v-else>
              <OrderCard 
                v-for="order in filteredPreparingOrders" 
                :key="order.id"
              :ref="`prepOrderCard_${order.id}`"
              :order="order"
              :isNew="false"
              :isVirtual="order.isVirtualOrder"
              :familyColor="order._familyColor || (order.parentOrderId ? getOrderFamilyColor(order.parentOrderId) : null)"
              :class="{ 'attention-animation': recentlyMovedOrders[order.id] }"
              @action="handleOrderAction"
              @viewDetails="showOrderDetails"
              @criticalAlert="handleCriticalAlert"
              />
            </template>
          </div>
        </div>
      </div>

    <!-- MODALS OUTSIDE SCALED INTERFACE - Normal size and positioning -->
    
    <!-- Order Details Modal -->
    <OrderDetailsModal 
      v-if="selectedOrder"
      :order="selectedOrder"
      @close="closeOrderDetails"
    />

    <!-- New Local Archive Modal -->
    <NewArchiveModal 
      :visible="showArchive"
      @close="closeArchive"
      @viewOrder="viewArchiveOrder"
    />

    <!-- Critical Order Alert Modal -->
    <CriticalOrderModal
      v-if="showCriticalAlert && criticalOrder"
      :order="criticalOrder"
      @prepare="handleCriticalPrepare"
      @acknowledge="handleCriticalAcknowledge"
      @close="closeCriticalAlert"
      @playSound="playCriticalSound"
      @stopSound="stopCriticalSound"
    />

    <!-- Feedback Modal -->
    <FeedbackModal
      v-if="showFeedbackModal"
      @close="closeFeedbackModal"
    />

    <!-- System Warning Modal -->
    <SystemWarningModal
      :isOpen="showSystemWarning"
      :shutdownDate="warningShutdownDate"
      :customWarningMessage="customWarningMessage"
      :currentLanguage="$store.state.language || 'de'"
      @close="closeSystemWarning"
    />

    <!-- Ownership Notice Modal -->
    <OwnershipNoticeModal
      :isOpen="showOwnershipNotice"
      :currentLanguage="$store.state.language || 'de'"
      :restaurantId="selectedRestaurantId"
      :userName="$store.state.selectedStation ? $store.state.selectedStation.name : 'Station User'"
      @close="handleOwnershipNoticeClose"
    />

    <!-- MealWave Connection Modal -->
    <MealWaveConnectionModal
      :isOpen="showMealWaveConnectionModal"
      @close="showMealWaveConnectionModal = false"
      @disconnected="handleMealWaveDisconnected"
    />

    <!-- Settings Modal -->
    <SettingsModal
      :isOpen="showSettingsModal"
      :audioUnlocked="audioUnlocked"
      @close="showSettingsModal = false"
      @showArchive="handleSettingsArchive"
      @changeStation="handleSettingsChangeStation"
      @logout="handleSettingsLogout"
      @testSound="unlockAudioManually"
      @previewUberModal="openUberModalPreview"
    />

    <!-- Uber Order Modal (for new incoming orders) -->
    <UberOrderModal
      :isOpen="showUberOrderModal"
      :isPreview="uberOrderModalPreview"
      :order="currentUberOrder || {}"
      @close="handleUberModalClose"
      @accept="handleUberOrderAccept"
      @deny="handleUberOrderDeny"
    />

    <!-- Uber Order Details Modal (for viewing/managing accepted orders) -->
    <UberOrderDetailsModal
      :isOpen="showUberOrderDetailsModal"
      :order="selectedUberOrder || {}"
      :language="$store.state.language || 'de'"
      @close="showUberOrderDetailsModal = false; selectedUberOrder = null"
      @cancel="handleUberOrderCancel"
      @out-of-stock="handleUberOutOfStock"
    />

  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import AppHeader from '@/components/AppHeader.vue';
import OrderCard from '@/components/OrderCard.vue';
import OrderDetailsModal from '@/components/OrderDetailsModal.vue';
import CriticalOrderModal from '@/components/CriticalOrderModal.vue';
import NewArchiveModal from '@/components/NewArchiveModal.vue';
import OfflineWarningModal from '@/components/OfflineWarningModal.vue';
import FeedbackModal from '@/components/FeedbackModal.vue';
import SystemWarningModal from '@/components/SystemWarningModal.vue';
import SuspendedScreen from '@/components/SuspendedScreen.vue';
import OwnershipNoticeModal from '@/components/OwnershipNoticeModal.vue';
import MealWaveConnectionModal from '@/components/MealWaveConnectionModal.vue';
import SettingsModal from '@/components/SettingsModal.vue';
import UberOrderModal from '@/components/UberOrderModal.vue';
import UberOrderDetailsModal from '@/components/UberOrderDetailsModal.vue';

export default {
  name: 'OrderDisplayView',
  components: {
    AppHeader,
    OrderCard,
    OrderDetailsModal,
    CriticalOrderModal,
    NewArchiveModal,
    OfflineWarningModal,
    FeedbackModal,
    SystemWarningModal,
    SuspendedScreen,
    OwnershipNoticeModal,
    MealWaveConnectionModal,
    SettingsModal,
    UberOrderModal,
    UberOrderDetailsModal
  },
  data() {
    return {
      showUberOrderModal: false,
      uberOrderModalPreview: false,
      currentUberOrder: null,
      uberEatsAudio: null, // Reference to looping Uber sound
      showUberOrderDetailsModal: false,
      selectedUberOrder: null,
      selectedOrder: null,
      ordersListener: null,
      mealwaveOrdersListener: null, // Listener for MealWave online orders
      mealwaveOrders: {}, // Store MealWave orders separately
      adminMessageListener: null,
      completedOrdersListener: null,
      showArchive: false,
      wasArchiveOpenBeforeDetails: false,
      showSettingsModal: false,

      // Header visibility state
      isHeaderVisible: false,
      autoHideTimer: null,

      // Critical order alert state
      criticalOrder: null,
      showCriticalAlert: false,
      acknowledgedCriticalOrders: new Set(),
      criticalSoundInterval: null,
      currentCriticalAudio: null,

      // Enhanced state management
      knownOrderIds: new Set(),
      knownAdditionIds: new Set(),
      startedAdditionIds: new Set(),
      ordersWithNewItems: new Set(),
      lastUpdateTime: 0,
      processingUpdate: false,
      errorState: {
        menuFetch: false,
        ordersFetch: false,
        lastError: null,
        retryCount: 0
      },

      // Queue system for handling high-volume updates
      updateQueue: [],
      isProcessingQueue: false,
      queueProcessor: null,
      lastProcessedSnapshot: null,
      pendingUpdates: [],
      
      // Admin message state
      adminMessage: {
        text: '',
        messageId: '',
        dismissed: false,
        lastChecked: 0
      },
      
      // Completed orders for archive
      completedOrders: {},
      
      // Family color tracking
      orderFamilyColors: {},

      // Track recently moved orders
      recentlyMovedOrders: {},

      // Sound system - simple and reliable
      previousOrderCount: 0,
      soundCooldown: false,
      preloadedAudio: null,
      audioUnlocked: false,

      // Feedback modal state
      showFeedbackModal: false,
      feedbackCheckTimer: null,

      // System warning modal state
      showSystemWarning: false,
      warningShutdownDate: null,
      customWarningMessage: '',
      isSystemSuspended: false,
      statusListener: null,

      // Ownership notice modal state
      showOwnershipNotice: false,

      // MealWave connection state
      showMealWaveConnectionModal: false,
      mealwaveConnectionStatus: null, // null, 'pending', 'approved', 'rejected'
      connectedMealWaveRestaurantId: null,
      mealwaveConnectionListener: null
    };
  },
  computed: {
    ...mapState(['selectedRestaurantId', 'selectedStation', 'orders', 'soundEnabled', 'menu', 'darkMode']),
    ...mapGetters(['filteredOrdersForStation']),
    newOrders() {
      const newOrders = this.processOrders().filter(order => 
        order.status === 'new'
      );
      
      // Group orders by family - additions should appear next to their parent
      const grouped = [];
      const processedIds = new Set();
      
      newOrders.forEach(order => {
        if (processedIds.has(order.id)) return;
        
        // If this is a parent order
        if (!order.parentOrderId) {
          grouped.push(order);
          processedIds.add(order.id);
          
          // Find all its children (additions) that are also in new orders
          const children = newOrders.filter(child => 
            child.parentOrderId === order.id && !processedIds.has(child.id)
          );
          
          // Add children right after parent
          children.forEach(child => {
            grouped.push(child);
            processedIds.add(child.id);
          });
        } else if (!processedIds.has(order.id)) {
          // This is an orphaned addition (parent not in new orders)
          grouped.push(order);
          processedIds.add(order.id);
        }
      });
      
      return grouped;
    },
    preparingOrders() {
      return this.processOrders().filter(order => 
        order.status === 'preparing'
      );
    },
    newOrdersCount() {
      return this.newOrders.length;
    },
    preparingOrdersCount() {
      return this.preparingOrders.length;
    },
    filteredNewOrders() {
      return this.newOrders.filter(order => this.shouldShowOrder(order));
    },
    filteredPreparingOrders() {
      return this.preparingOrders.filter(order => this.shouldShowOrder(order));
    },
    sortedCompletedOrders() {
      return Object.entries(this.completedOrders)
        .sort(([, a], [, b]) => {
          const timeA = a.archivedAt || a.completedAt || 0;
          const timeB = b.archivedAt || b.completedAt || 0;
          return new Date(timeB) - new Date(timeA); // Most recent first
        })
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
    }
  },
  mounted() {
    if (!this.selectedRestaurantId || !this.selectedStation) {
      this.$router.push('/login');
      return;
    }

    // Initialize combinedOrders in store if empty
    if (Object.keys(this.$store.state.combinedOrders).length === 0) {
      this.$store.commit('BATCH_UPDATE_ALL_ORDERS', {
        orders: {},
        virtualOrders: []
      });
    }

    this.initializeOrderDisplay();
    // Connect directly to MealWave orders
    this.listenForMealWaveOrders(this.selectedRestaurantId);
    this.setupConnectionListener();
    // this.startFeedbackTimer(); // DISABLED - Feedback modal at 23:10

    // Listen to Arabic language setting
    this.$store.dispatch('listenToArabicSetting');

    // Check for Table Tech connections and listen to Table Tech orders
    this.$store.dispatch('listenToTableTechOrders');

    // Start the queue processor for order updates
    this.startQueueProcessor();

    // Preload audio for instant playback
    this.preloadedAudio = new Audio('/SoundNoti.mp3');
    this.preloadedAudio.load();

    // Initialize previous order count to current count (don't play sound on page load)
    this.$nextTick(() => {
      this.previousOrderCount = this.newOrders?.length || 0;
    });

    // Unlock audio on first user interaction (browser requirement)
    const unlockAudio = () => {
      if (!this.audioUnlocked) {
        // Play silent sound to unlock audio
        const silentAudio = new Audio('/SoundNoti.mp3');
        silentAudio.volume = 0;
        silentAudio.play().then(() => {
          this.audioUnlocked = true;
          console.log('🔊 Audio unlocked');
        }).catch(() => {});
      }
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
  },
  beforeDestroy() {
    if (this.ordersListener) {
      this.ordersListener();
    }
    if (this.mealwaveOrdersListener) {
      this.mealwaveOrdersListener();
    }
    if (this.mealwaveConnectionListener) {
      this.mealwaveConnectionListener();
    }
    if (this.adminMessageListener) {
      this.adminMessageListener();
    }
    if (this.completedOrdersListener) {
      this.completedOrdersListener();
    }
    if (this.statusListener) {
      this.statusListener();
    }
    // Stop Table Tech orders listener
    this.$store.dispatch('stopTableTechOrdersListener');
    // Stop queue processor
    if (this.queueProcessor) {
      clearInterval(this.queueProcessor);
      this.queueProcessor = null;
    }
    // Clear sound debounce timer
    if (this.soundDebounceTimer) {
      clearTimeout(this.soundDebounceTimer);
      this.soundDebounceTimer = null;
    }
    // Stop critical sound if still playing
    this.stopCriticalSound();
    // Clear auto-hide timer
    this.clearAutoHideTimer();
    // Clear feedback timer
    if (this.feedbackCheckTimer) {
      clearInterval(this.feedbackCheckTimer);
      this.feedbackCheckTimer = null;
    }
  },
  watch: {
    // Simple and reliable sound system - watch order count
    newOrders: {
      handler(newVal) {
        const currentCount = newVal?.length || 0;

        // Play sound if count increased and sound is enabled
        if (currentCount > this.previousOrderCount && this.soundEnabled && !this.soundCooldown) {
          console.log('🔔 New order detected! Count:', this.previousOrderCount, '→', currentCount);
          this.playOrderSound();

          // Set cooldown to prevent sound spam
          this.soundCooldown = true;
          setTimeout(() => {
            this.soundCooldown = false;
          }, 2000); // 2 second cooldown
        }

        // Update previous count
        this.previousOrderCount = currentCount;
      },
      deep: false
    }
  },
  methods: {
    ...mapActions(['loadMenu', 'updateOrders', 'setConnectionStatus', 'startPreparingOrder', 'completeOrder']),
    
    // Header management methods
    showHeader() {
      this.isHeaderVisible = true;
      this.startAutoHideTimer();
    },
    
    hideHeader() {
      this.isHeaderVisible = false;
      this.clearAutoHideTimer();
    },
    
    startAutoHideTimer() {
      this.clearAutoHideTimer();
      this.autoHideTimer = setTimeout(() => {
        this.isHeaderVisible = false;
      }, 8000); // 8 seconds
    },
    
    clearAutoHideTimer() {
      if (this.autoHideTimer) {
        clearTimeout(this.autoHideTimer);
        this.autoHideTimer = null;
      }
    },
    
    resetAutoHideTimer() {
      if (this.isHeaderVisible) {
        this.startAutoHideTimer();
      }
    },
    
    initializeOrderDisplay() {
      this.loadMenu();
    },
    
    async listenForOrders() {
      const { ref, onValue } = await import('firebase/database');
      const db = this.$firebase.database();
      const ordersRef = ref(db, `restaurants/${this.selectedRestaurantId}/orders`);

      this.ordersListener = onValue(ordersRef, (snapshot) => {
        // Queue the update instead of processing immediately
        this.queueUpdate(snapshot);
      });

      // Start the queue processor
      this.startQueueProcessor();
    },

    // Check MealWave connection status and listen for orders if approved
    async checkMealWaveConnection() {
      try {
        const { ref, onValue } = await import('firebase/database');
        const mealwaveDb = this.$firebase.mealwaveDatabase();

        // Listen to all connections for this Table Tech restaurant
        const connectionsRef = ref(mealwaveDb, `mealwave_connections`);

        this.mealwaveConnectionListener = onValue(connectionsRef, (snapshot) => {
          const allConnections = snapshot.val() || {};

          // Search for any approved connection involving this Table Tech restaurant
          for (const [mealwaveId, connections] of Object.entries(allConnections)) {
            if (connections[this.selectedRestaurantId]) {
              const connection = connections[this.selectedRestaurantId];
              this.mealwaveConnectionStatus = connection.status;

              if (connection.status === 'approved') {
                this.connectedMealWaveRestaurantId = mealwaveId;
                // Update store with connected MealWave restaurant ID
                this.$store.commit('SET_MEALWAVE_RESTAURANT_ID', mealwaveId);
                // Start listening for MealWave orders
                this.listenForMealWaveOrders(mealwaveId);
                console.log('📱 MealWave connection approved, listening for orders from:', mealwaveId);
              } else {
                // Stop listening if not approved
                if (this.mealwaveOrdersListener) {
                  this.mealwaveOrdersListener();
                  this.mealwaveOrdersListener = null;
                }
                this.mealwaveOrders = {};
                console.log('📱 MealWave connection status:', connection.status);
              }
              return;
            }
          }

          // No connection found
          this.mealwaveConnectionStatus = null;
          this.connectedMealWaveRestaurantId = null;
          this.$store.commit('SET_MEALWAVE_RESTAURANT_ID', null);
          if (this.mealwaveOrdersListener) {
            this.mealwaveOrdersListener();
            this.mealwaveOrdersListener = null;
          }
          this.mealwaveOrders = {};
        });
      } catch (error) {
        console.error('Error checking MealWave connection:', error);
      }
    },

    // Listen for MealWave online orders
    async listenForMealWaveOrders(restaurantId) {
      // Stop any existing listener
      if (this.mealwaveOrdersListener) {
        this.mealwaveOrdersListener();
      }

      if (!restaurantId) {
        console.log('📱 No restaurant ID provided');
        return;
      }

      console.log('📱 Setting up MealWave order listener for:', restaurantId);

      const { ref, onValue } = await import('firebase/database');
      const db = this.$firebase.database();
      // Path: restaurants/{restaurantId}/live_orders
      const mealwaveOrdersRef = ref(db, `restaurants/${restaurantId}/live_orders`);

      this.mealwaveOrdersListener = onValue(mealwaveOrdersRef, (snapshot) => {
        const mealwaveOrders = snapshot.val() || {};
        console.log('📱 [LISTENER] MealWave orders received:', Object.keys(mealwaveOrders).length);

        // Process MealWave orders and update the store directly (same pattern as Table Tech)
        this.processMealWaveOrders(mealwaveOrders);
      });
    },

    // Process MealWave orders directly
    processMealWaveOrders(mealwaveOrders) {
      const currentStation = this.selectedStation?.destinationCode;
      if (!currentStation) {
        console.log('📱 No station selected, skipping order processing');
        return;
      }

      const processedOrders = {};
      let hasNewOrder = false;

      Object.entries(mealwaveOrders).forEach(([orderId, order]) => {
        // Skip placeholder entries
        if (order === true || !order || order.placeholder) return;

        // Check if this order has items for the current station
        const stationSection = order[currentStation];
        if (!stationSection || !stationSection.items || stationSection.items.length === 0) {
          return;
        }

        // Check if this is a new order we haven't seen before
        const mealwaveOrderId = `mealwave_${orderId}`;
        if (!this.knownOrderIds.has(mealwaveOrderId)) {
          this.knownOrderIds.add(mealwaveOrderId);
          console.log('🔔 New MealWave order detected:', orderId);

          // Auto-trigger Uber modal for pending Uber orders (needs acceptance)
          if (order.source === 'ubereats' && (order.status === 'pending' || stationSection.status === 'Pending')) {
            console.log('🍔 New Uber Eats order - triggering acceptance modal:', orderId);
            this.$nextTick(() => {
              this.currentUberOrder = {
                ...order,
                id: orderId,
                orderId: orderId,
                restaurantId: this.connectedMealWaveRestaurantId || this.$store.state.mealwaveRestaurantId
              };
              this.uberOrderModalPreview = false;
              this.showUberOrderModal = true;
              // Play Uber Eats sound effect
              this.playUberEatsSound();
            });
            // Don't count pending Uber orders as new orders for sound
          } else {
            hasNewOrder = true;
          }
        }

        // Skip pending Uber orders - they shouldn't appear in the order list yet
        if (order.source === 'ubereats' && (order.status === 'pending' || stationSection.status === 'Pending')) {
          console.log('📋 Skipping pending Uber order from display:', orderId);
          return;
        }

        const mappedStatus = this.mapMealWaveStatus(stationSection.status || order.status);

        // Transform MealWave order to display format
        processedOrders[orderId] = {
          ...order,
          id: orderId,
          orderId: orderId,
          source: order.source || 'mealwave',
          isMealWaveOrder: true,
          tableNumber: order.type === 'delivery' ? 'Lieferung' : 'Abholung',
          orderNumber: order.orderNumber || orderId.replace('order-', ''),
          timestamp: order.timestamp || order.payment?.paidAt || Date.now(),
          waiter: { name: 'Online' },
          _familyColor: null,
          _hasChildren: false,
          [currentStation]: {
            ...stationSection,
            status: mappedStatus
          }
        };
      });

      // Play sound for new MealWave orders
      if (hasNewOrder && this.soundEnabled) {
        this.playNewOrderSound();
      }

      // Update local cache
      this.mealwaveOrders = processedOrders;

      // Create a fake snapshot to use the existing queue system
      const fakeSnapshot = {
        val: () => ({})
      };

      // Trigger the queue system
      this.queueUpdate(fakeSnapshot);

      // Play sound for new orders
      Object.keys(processedOrders).forEach(orderId => {
        if (!this.knownOrderIds.has(orderId)) {
          this.knownOrderIds.add(orderId);
          if (this.soundEnabled) {
            this.playNewOrderSound();
          }
        }
      });
    },

    // Map MealWave status to Table Tech status
    mapMealWaveStatus(status) {
      const statusMap = {
        'new': 'New',
        'New': 'New',
        'preparing': 'Preparing',
        'Preparing': 'Preparing',
        'ready': 'Ready',
        'Ready': 'Ready',
        'completed': 'Completed',
        'Completed': 'Completed'
      };
      return statusMap[status] || 'New';
    },

    queueUpdate(snapshot) {
      // Add snapshot to queue with timestamp
      this.updateQueue.push({
        snapshot: snapshot,
        timestamp: Date.now()
      });

      // Process immediately if not already processing
      if (!this.isProcessingQueue) {
        // Use nextTick to ensure DOM has updated from previous changes
        this.$nextTick(() => {
          this.processNextInQueue();
        });
      }
    },

    startQueueProcessor() {
      // Process queue every 100ms to batch updates
      if (!this.queueProcessor) {
        this.queueProcessor = setInterval(() => {
          if (!this.isProcessingQueue && this.updateQueue.length > 0) {
            this.processNextInQueue();
          }
        }, 100);
      }
    },

    async processNextInQueue() {
      if (this.isProcessingQueue) return;

      this.isProcessingQueue = true;

      // Process ALL snapshots in FIFO order (oldest first)
      while (this.updateQueue.length > 0) {
        const { snapshot } = this.updateQueue.shift(); // Take oldest first
        try {
          await this.processOrderUpdate(snapshot);
        } catch (error) {
          console.error('Error processing order update:', error);
        }
      }

      this.isProcessingQueue = false;
    },

    async processOrderUpdate(snapshot) {
      const firebaseOrders = snapshot.val() || {};
      const startTime = performance.now();

      // Step 1: Update main orders and virtual orders together
      // This ensures no gap where virtual orders are missing
      const ordersWithFamilyColors = this.batchProcessOrders(firebaseOrders);
      const virtualOrdersList = this.prepareVirtualOrders(firebaseOrders);

      // Step 1.5: Merge MealWave orders with Table Tech orders
      // This ensures MealWave orders don't get wiped out when Table Tech orders update
      // Use fresh copy of mealwaveOrders to ensure latest status is included
      Object.entries(this.mealwaveOrders).forEach(([orderId, order]) => {
        ordersWithFamilyColors[orderId] = {
          ...order,
          _familyColor: null,
          _hasChildren: false
        };
      });
      console.log('📱 [MEALWAVE] Merged', Object.keys(this.mealwaveOrders).length, 'MealWave orders into processOrderUpdate');

      // Step 2: Commit everything in one batch to minimize reactivity cycles
      this.$store.commit('BATCH_UPDATE_ALL_ORDERS', {
        orders: ordersWithFamilyColors,
        virtualOrders: virtualOrdersList
      });

      // Play sound for new orders (not debounced for immediate feedback)
      if (this.soundEnabled && this.checkForNewOrdersOrAdditions(firebaseOrders)) {
        this.playNewOrderSound();
      }

      this.lastUpdateTime = Date.now();
      const processingTime = performance.now() - startTime;

      if (processingTime > 500) {
        console.warn(`Order processing took ${processingTime.toFixed(2)}ms - consider optimization`);
      }
    },

    batchProcessOrders(firebaseOrders) {
      const ordersWithFamilyColors = {};
      const entries = Object.entries(firebaseOrders);

      // Process in chunks to prevent blocking
      for (let i = 0; i < entries.length; i++) {
        const [orderId, order] = entries[i];
        const hasChildren = order.additions && Object.keys(order.additions).length > 0;

        ordersWithFamilyColors[orderId] = {
          ...order,
          _familyColor: hasChildren ? this.getOrderFamilyColor(orderId) : null,
          _hasChildren: hasChildren
        };
      }

      return ordersWithFamilyColors;
    },

    prepareVirtualOrders(firebaseOrders) {
      const currentStation = this.selectedStation?.destinationCode;
      if (!currentStation) return [];

      const virtualOrders = [];

      Object.entries(firebaseOrders).forEach(([parentOrderId, parentOrder]) => {
        if (!parentOrder?.additions) return;

        const familyColor = this.getOrderFamilyColor(parentOrderId);
        const parentSection = parentOrder[currentStation];
        const isParentNew = parentSection?.status === 'New';

        Object.entries(parentOrder.additions).forEach(([additionId, addition]) => {
          if (addition[currentStation]?.items?.length > 0) {
            const virtualOrderId = `${parentOrderId}_${additionId}`;
            const isNew = !this.knownAdditionIds.has(virtualOrderId);

            const virtualOrder = {
              [currentStation]: addition[currentStation],
              id: virtualOrderId,
              parentOrderId: parentOrderId,
              tableNumber: parentOrder.tableNumber,
              orderNumber: parentOrder.orderNumber,
              waiter: addition.addedBy || parentOrder.waiter,
              timestamp: addition.timestamp || Date.now(),
              orderType: parentOrder.orderType,
              table: parentOrder.table,
              isAddition: true,
              isVirtualOrder: true,
              familyColor: familyColor,
              birthAnimation: isNew,
              birthNextToMom: isParentNew && isNew
            };

            virtualOrders.push({ id: virtualOrderId, order: virtualOrder });

            if (isNew) {
              this.knownAdditionIds.add(virtualOrderId);
            }
          }
        });
      });

      return virtualOrders;
    },

    batchProcessVirtualOrders(firebaseOrders) {
      const currentStation = this.selectedStation?.destinationCode;
      if (!currentStation) return;

      const virtualOrders = [];
      const newAdditions = [];

      Object.entries(firebaseOrders).forEach(([parentOrderId, parentOrder]) => {
        if (!parentOrder?.additions) return;

        const familyColor = this.getOrderFamilyColor(parentOrderId);
        const parentSection = parentOrder[currentStation];
        const isParentNew = parentSection?.status === 'New';

        Object.entries(parentOrder.additions).forEach(([additionId, addition]) => {
          if (addition[currentStation]?.items?.length > 0) {
            const virtualOrderId = `${parentOrderId}_${additionId}`;
            const isNew = !this.knownAdditionIds.has(virtualOrderId);

            const virtualOrder = {
              [currentStation]: addition[currentStation],
              id: virtualOrderId,
              parentOrderId: parentOrderId,
              tableNumber: parentOrder.tableNumber,
              orderNumber: parentOrder.orderNumber,
              waiter: addition.addedBy || parentOrder.waiter,
              timestamp: addition.timestamp || Date.now(),
              orderType: parentOrder.orderType,
              table: parentOrder.table,
              isAddition: true,
              isVirtualOrder: true,
              familyColor: familyColor,
              birthAnimation: isNew,
              birthNextToMom: isParentNew && isNew
            };

            virtualOrders.push({ id: virtualOrderId, order: virtualOrder });

            if (isNew) {
              newAdditions.push({ parentOrderId, virtualOrderId });
              this.knownAdditionIds.add(virtualOrderId);
            }
          }
        });
      });

      // Batch commit all virtual orders
      if (virtualOrders.length > 0) {
        this.$store.commit('BATCH_ADD_VIRTUAL_ORDERS', virtualOrders);
      }

      // Batch trigger parent animations
      if (newAdditions.length > 0) {
        requestAnimationFrame(() => {
          newAdditions.forEach(({ parentOrderId }) => {
            this.triggerParentGlow(parentOrderId);
          });
          if (newAdditions.length === 1) {
            this.playBirthSound();
          }
        });
      }
    },

    
    async listenForAdminMessages() {
      const { ref, onValue } = await import('firebase/database');
      const db = this.$firebase.database();
      const currentStation = this.selectedStation?.destinationCode || 'kitchen';
      const stationKey = currentStation.toLowerCase(); // K -> k, D -> d, O -> o
      const messageRef = ref(db, `restaurants/${this.selectedRestaurantId}/admin_messages/${stationKey}`);
      
      this.adminMessageListener = onValue(messageRef, (snapshot) => {
        const messageData = snapshot.val();
        if (messageData) {
          if (messageData.id !== this.adminMessage.messageId) {
            this.adminMessage = {
              text: messageData.message,
              messageId: messageData.id,
              dismissed: false,
              lastChecked: Date.now()
            };
            
            // Play sound for new admin message
            this.playAdminMessageSound();
          }
        } else {
          this.adminMessage.text = '';
        }
      });
    },
    
    async setupConnectionListener() {
      const { ref, onValue } = await import('firebase/database');
      const db = this.$firebase.database();
      const connectedRef = ref(db, '.info/connected');
      
      onValue(connectedRef, (snap) => {
        const connected = snap.val() === true;
        this.setConnectionStatus(connected);
      });
    },
    
    processAdditionsIntoVirtualOrders(allOrders) {
      const currentStation = this.selectedStation?.destinationCode;
      if (!currentStation) return;
      
      Object.entries(allOrders).forEach(([parentOrderId, parentOrder]) => {
        if (!parentOrder?.additions) return;
        
        // Get or generate family color for this order
        const familyColor = this.getOrderFamilyColor(parentOrderId);
        
        Object.entries(parentOrder.additions).forEach(([additionId, addition]) => {
          // Check if this addition has items for current station
          if (addition[currentStation]?.items?.length > 0) {
            const virtualOrderId = `${parentOrderId}_${additionId}`;
            
            // Check if parent is in new orders section
            const parentSection = parentOrder[currentStation];
            const isParentNew = parentSection?.status === 'New';
            
            const virtualOrder = {
              // Station-specific data (items, status)
              [currentStation]: addition[currentStation],
              
              // Metadata from parent
              id: virtualOrderId,
              parentOrderId: parentOrderId,
              tableNumber: parentOrder.tableNumber,
              orderNumber: parentOrder.orderNumber,
              waiter: addition.addedBy || parentOrder.waiter,
              timestamp: addition.timestamp || Date.now(),
              
              // Copy other necessary fields from parent
              orderType: parentOrder.orderType,
              table: parentOrder.table,
              
              // Addition-specific flags
              isAddition: true,
              isVirtualOrder: true,
              familyColor: familyColor,
              birthAnimation: !this.knownAdditionIds.has(virtualOrderId),
              birthNextToMom: isParentNew && !this.knownAdditionIds.has(virtualOrderId)
            };
            
            this.$store.commit('ADD_VIRTUAL_ORDER', { 
              id: virtualOrderId, 
              order: virtualOrder 
            });
            
            // Trigger parent glow animation if this is a new addition
            if (virtualOrder.birthAnimation) {
              this.triggerParentGlow(parentOrderId);
              // Play birth sound
              this.playBirthSound();
              // Track that we've seen this addition
              this.knownAdditionIds.add(virtualOrderId);
            }
          }
        });
      });
    },
    
    triggerParentGlow(parentOrderId) {
      // Add shaking and glowing to parent order card
      this.$nextTick(() => {
        const parentCard = document.querySelector(`[data-order-id="${parentOrderId}"]`);
        if (parentCard) {
          // First shake
          parentCard.classList.add('parent-shaking');
          
          // Then glow after shake
          setTimeout(() => {
            parentCard.classList.remove('parent-shaking');
            parentCard.classList.add('parent-glowing');
            
            // Remove glow after animation
            setTimeout(() => {
              parentCard.classList.remove('parent-glowing');
            }, 1000);
          }, 500);
        }
      });
    },
    
    getNewAdditionsForSection(items, orderId, section) {
      if (!items) return [];
      
      const newItems = [];
      items.forEach(item => {
        const itemId = `${orderId}_${section}_${item.articleNumber}_${item.timestamp || Date.now()}`;
        if (!this.knownAdditionIds.has(itemId)) {
          this.knownAdditionIds.add(itemId);
          newItems.push(item);
        }
      });
      
      return newItems;
    },
    
    createAdditionOrder(originalOrderId, originalOrder, newItemsBySection) {
      // Create a new order for additions
      const additionOrderId = `${originalOrderId}_ADD_${Date.now()}`;
      
      const additionOrder = {
        id: additionOrderId,
        tableNumber: originalOrder.tableNumber,
        orderNumber: `${originalOrder.orderNumber || originalOrderId.substring(0, 6)}-إضافة`,
        timestamp: Date.now(),
        waiter: originalOrder.waiter,
        isAddition: true,
        originalOrderId: originalOrderId,
        status: 'pending'
      };
      
      // Add items to their respective sections
      Object.entries(newItemsBySection).forEach(([section, items]) => {
        additionOrder[section] = {
          items: items,
          status: 'New'
        };
      });
      
      // Add to store as a regular order
      this.$store.commit('ADD_ORDER', { id: additionOrderId, order: additionOrder });
    },
    
    processOrders() {
      // Process all orders (including Table Tech and MealWave orders)
      const processedOrders = [];

      Object.entries(this.filteredOrdersForStation).forEach(([orderId, order]) => {
        const orderStatus = this.getOrderStatus(order);
        processedOrders.push({
          ...order,
          id: orderId,
          status: orderStatus.status,
          statusText: orderStatus.text,
          statusColor: orderStatus.color
        });
      });

      // Sort by timestamp
      return processedOrders.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    },
    
    getOrderStatus(order) {
      if (!this.selectedStation?.destinationCode) {
        return { text: this.$t('statusNew'), color: 'blue', status: 'new' };
      }
      
      const section = order[this.selectedStation.destinationCode];
      const sectionStatus = section?.status;
      
      if (sectionStatus === 'Cancelled') {
        return { text: this.$t('statusCancelled'), color: 'red', status: 'cancelled' };
      } else if (sectionStatus === 'Completed' || sectionStatus === 'Ready') {
        return { text: this.$t('statusReady'), color: 'green', status: 'completed' };
      } else if (sectionStatus === 'Preparing' || sectionStatus === 'In Progress') {
        return { text: this.$t('statusPreparing'), color: 'yellow', status: 'preparing' };
      } else if (sectionStatus === 'New' || sectionStatus === 'Added') {
        return { text: this.$t('statusNew'), color: 'blue', status: 'new' };
      }
      
      // Check if all items are cancelled, if so, treat as cancelled
      if (section && section.items && section.items.length > 0 && 
          section.items.every(item => item.cancelled === true)) {
        return { text: this.$t('statusCancelled'), color: 'red', status: 'cancelled' };
      }
      
      return { text: this.$t('statusNew'), color: 'blue', status: 'new' };
    },
    
    handleOrderAction(orderId, action) {
      if (action === 'prepare') {
        // Mark this order as recently moved so it gets the attention animation
        this.recentlyMovedOrders[orderId] = Date.now();
        
        // Remove the animation after delay
        setTimeout(() => {
          delete this.recentlyMovedOrders[orderId];
        }, 1500); // Animation duration (1s) + little extra
        
        // Update Firebase status
        this.startPreparingOrder(orderId);
      } else if (action === 'complete') {
        this.completeOrder(orderId);
      } else if (action === 'uberReady') {
        this.markUberOrderReady(orderId);
      }
    },

    async markUberOrderReady(orderId) {
      try {
        // Get the order from orders object
        const order = this.orders[orderId];
        if (!order) {
          console.error('Order not found:', orderId);
          return;
        }

        console.log('Marking Uber order ready:', orderId);

        const response = await fetch('https://api.meal-wave.com/api/uber/ready', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: orderId,
            storeId: order.uberStoreId || order.storeId,
            restaurantId: order.restaurantId
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          console.log('✅ Uber order marked as ready for pickup');
          // The server updates Firebase, which will trigger UI update
        } else {
          console.error('Failed to mark order ready:', data.error);
          alert('Failed to mark order ready: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error marking Uber order ready:', error);
        alert('Error: ' + error.message);
      }
    },


    showOrderDetails(order) {
      // Check if it's an Uber Eats order - show special modal
      if (order.source === 'ubereats') {
        this.selectedUberOrder = order;
        this.showUberOrderDetailsModal = true;
        return;
      }
      // Regular orders use the normal details modal
      this.selectedOrder = order;
    },
    
    // handleEditOrder method removed - edit is now only done from details modal
    
    closeOrderDetails() {
      // Since we're now updating the database directly, we don't need 
      // to manually update the orders data - Firebase will push updates
      
      this.selectedOrder = null;
      
      // Restore archive modal if it was open before viewing details
      if (this.wasArchiveOpenBeforeDetails) {
        this.showArchive = true;
        this.wasArchiveOpenBeforeDetails = false;
      }
    },
    
    // Check if an order should be displayed or hidden for the current station
    shouldShowOrder(order) {
      if (!order) return false;
      
      // Only check the status for the current station
      const destinationCode = this.selectedStation?.destinationCode;
      if (!destinationCode) return true;
      
      const section = order[destinationCode];
      
      // If this station's section is cancelled, hide the order card
      if (section && section.status === 'Cancelled') {
        console.log(`Order station ${destinationCode} cancelled, hiding card:`, order.id);
        return false;
      }
      
      // If all items for this station are cancelled, treat as station cancelled
      if (section && section.items) {
        const allCancelled = section.items.length > 0 && 
                            section.items.every(item => item.cancelled === true);
        if (allCancelled) {
          console.log(`All items for station ${destinationCode} are cancelled, hiding:`, order.id);
          
          // If all items are cancelled but status isn't set, update it for consistency
          if (section.status !== 'Cancelled') {
            console.log(`Updating status to Cancelled for order station ${destinationCode}:`, order.id);
            this.$store.dispatch('cancelOrder', {
              orderId: order.id,
              reason: 'All items for this station were cancelled'
            }).catch(error => {
              console.error('Error updating order status:', error);
            });
          }
          return false;
        }
      }
      
      return true;
    },
    
    async showArchiveModal() {
      console.log('🗄️ Archive button clicked, opening modal...');
      this.showArchive = true;
      
      // Load archive data when modal is opened
      await this.loadCompletedOrders();
    },
    
    closeArchive() {
      this.showArchive = false;
    },

    // Settings Modal Handlers
    handleSettingsArchive() {
      this.showSettingsModal = false;
      this.showArchiveModal();
    },

    handleSettingsChangeStation() {
      this.showSettingsModal = false;
      this.$router.push('/station-selection');
    },

    async handleSettingsLogout() {
      this.showSettingsModal = false;
      await this.$store.dispatch('logout');
      localStorage.clear();
      this.$router.replace('/login');
    },

    // Uber Order Modal Methods
    openUberModalPreview() {
      this.showSettingsModal = false;
      this.uberOrderModalPreview = true;
      this.showUberOrderModal = true;
    },

    async handleUberOrderAccept(data) {
      console.log('Uber order accepted:', data);

      if (!this.currentUberOrder) {
        console.error('No current Uber order to accept');
        this.showUberOrderModal = false;
        return;
      }

      try {
        const restaurantId = this.currentUberOrder.restaurantId || this.connectedMealWaveRestaurantId || this.$store.state.mealwaveRestaurantId;
        const storeId = this.currentUberOrder.uberStoreId;
        console.log('Accepting order - restaurantId:', restaurantId, 'storeId:', storeId);

        const response = await fetch('https://api.meal-wave.com/api/uber/accept', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: this.currentUberOrder.orderId || this.currentUberOrder.id,
            restaurantId: restaurantId,
            storeId: storeId,
            prepTime: data.prepTime || 15
          })
        });

        const result = await response.json();
        console.log('Uber accept response:', result);

        if (result.success) {
          console.log(`Order accepted! Ready at: ${result.readyTime}`);
        } else {
          console.error('Failed to accept order:', result.error);
        }
      } catch (error) {
        console.error('Error accepting Uber order:', error);
      }

      this.stopUberEatsSound(); // Stop the looping sound
      this.showUberOrderModal = false;
      this.currentUberOrder = null;
    },

    async handleUberOrderDeny(data) {
      console.log('Uber order denied:', data);

      if (!this.currentUberOrder) {
        console.error('No current Uber order to deny');
        this.showUberOrderModal = false;
        return;
      }

      try {
        const restaurantId = this.currentUberOrder.restaurantId || this.connectedMealWaveRestaurantId || this.$store.state.mealwaveRestaurantId;
        const storeId = this.currentUberOrder.uberStoreId;
        console.log('Denying order - restaurantId:', restaurantId, 'storeId:', storeId);

        const response = await fetch('https://api.meal-wave.com/api/uber/deny', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: this.currentUberOrder.orderId || this.currentUberOrder.id,
            restaurantId: restaurantId,
            storeId: storeId,
            reason: data.reason || 'other'
          })
        });

        const result = await response.json();
        console.log('Uber deny response:', result);

        if (result.success) {
          console.log('Order denied successfully');
        } else {
          console.error('Failed to deny order:', result.error);
        }
      } catch (error) {
        console.error('Error denying Uber order:', error);
      }

      this.stopUberEatsSound(); // Stop the looping sound
      this.showUberOrderModal = false;
      this.currentUberOrder = null;
    },

    handleUberModalClose() {
      this.stopUberEatsSound(); // Stop the looping sound
      this.showUberOrderModal = false;
      this.currentUberOrder = null;
    },

    // Cancel an accepted Uber order
    async handleUberOrderCancel(data) {
      console.log('Cancel order requested:', data);

      try {
        const restaurantId = this.selectedUberOrder.restaurantId || this.connectedMealWaveRestaurantId || this.$store.state.mealwaveRestaurantId;
        const storeId = this.selectedUberOrder.uberStoreId;

        const response = await fetch('https://api.meal-wave.com/api/uber/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: this.selectedUberOrder.orderId || this.selectedUberOrder.id,
            restaurantId: restaurantId,
            storeId: storeId,
            reason: data.reason || 'other'
          })
        });

        const result = await response.json();
        console.log('Uber cancel response:', result);

        if (result.success) {
          console.log('Order cancelled successfully');
        } else {
          console.error('Failed to cancel order:', result.error);
        }
      } catch (error) {
        console.error('Error cancelling Uber order:', error);
      }

      this.showUberOrderDetailsModal = false;
      this.selectedUberOrder = null;
    },

    handleUberOutOfStock() {
      console.log('Out of stock requested for order:', this.selectedUberOrder?.orderId);
      // TODO: Implement out of stock flow
      alert('Artikel nicht verfügbar wird implementiert...');
    },

    viewArchiveOrder(order, orderId) {
      // Remember that archive was open before viewing details
      this.wasArchiveOpenBeforeDetails = this.showArchive;
      
      // Close archive modal
      this.showArchive = false;
      
      // Show order details for archive orders
      const orderWithId = {
        ...order,
        id: orderId,
        orderNumber: order.orderNumber || orderId.substring(0, 6)
      };
      this.selectedOrder = orderWithId;
    },
    
    formatCompletionTime(timestamp) {
      if (!timestamp) return '';
      
      const date = typeof timestamp === 'string' 
        ? new Date(timestamp) 
        : new Date(parseInt(timestamp));
      
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `منذ ${diffHours} ساعة${diffMinutes > 0 ? ` و ${diffMinutes} دقيقة` : ''}`;
      } else {
        return `منذ ${diffMinutes} دقيقة`;
      }
    },
    
    getOrderItemsForDisplay(order) {
      if (!this.selectedStation?.destinationCode) return [];
      
      const stationCode = this.selectedStation.destinationCode;
      const stationSection = order[stationCode];
      
      return stationSection?.items || [];
    },
    
    getItemDisplayName(item) {
      if (!item) return 'Unknown Item';
      
      // Try to get the menu item by article number
      const menuItem = this.getMenuItemByArticleNumber ? this.getMenuItemByArticleNumber(item.articleNumber) : null;
      
      // Always prioritize Arabic names
      if (menuItem) {
        return menuItem.name_ar || menuItem.name || item.name_ar || item.name || item.articleNumber || 'Unknown Item';
      }
      
      return item.name_ar || item.name || item.articleNumber || 'Unknown Item';
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '';
      
      const date = typeof timestamp === 'string' 
        ? new Date(timestamp) 
        : new Date(parseInt(timestamp));
      
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    
    dismissMessage() {
      this.adminMessage.dismissed = true;
    },
    
    checkForNewOrdersOrAdditions(firebaseOrders) {
      let hasNew = false;
      const currentStation = this.selectedStation?.destinationCode;
      
      if (!currentStation) return false;
      
      // Check for new main orders that have items for current station
      Object.entries(firebaseOrders).forEach(([orderId, order]) => {
        if (!this.knownOrderIds.has(orderId)) {
          this.knownOrderIds.add(orderId);
          
          // Only trigger sound if this order has items for current station
          if (order[currentStation]?.items?.length > 0) {
            hasNew = true;
          }
        }
      });
      
      // Check for new additions that have items for current station
      Object.entries(firebaseOrders).forEach(([orderId, order]) => {
        if (order?.additions) {
          Object.entries(order.additions).forEach(([additionId, addition]) => {
            const virtualOrderId = `${orderId}_${additionId}`;
            if (!this.knownOrderIds.has(virtualOrderId)) {
              this.knownOrderIds.add(virtualOrderId);
              
              // Only trigger sound if this addition has items for current station
              if (addition[currentStation]?.items?.length > 0) {
                hasNew = true;
              }
            }
          });
        }
      });
      
      return hasNew;
    },
    
    playNewOrderSound() {
      // Old method - kept for compatibility
      this.playOrderSound();
    },

    // New simple and reliable sound method
    playOrderSound() {
      if (!this.soundEnabled) return;

      try {
        // Use preloaded audio for instant playback
        if (this.preloadedAudio) {
          this.preloadedAudio.currentTime = 0;
          this.preloadedAudio.volume = 0.8;
          this.preloadedAudio.play().catch(err => {
            console.warn('Preloaded audio failed, trying fresh audio:', err);
            this.playFreshAudio();
          });
        } else {
          this.playFreshAudio();
        }
      } catch (error) {
        console.error('Error playing sound:', error);
        this.playFreshAudio();
      }
    },

    playFreshAudio() {
      try {
        const audio = new Audio('/SoundNoti.mp3');
        audio.volume = 0.8;
        audio.play().catch(error => {
          console.error('Error playing fresh audio:', error);
        });
      } catch (error) {
        console.error('Error creating audio:', error);
      }
    },

    unlockAudioManually() {
      const audio = new Audio('/SoundNoti.mp3');
      audio.volume = 0.5;
      audio.play().then(() => {
        this.audioUnlocked = true;
        console.log('🔊 Audio manually unlocked');
      }).catch(err => {
        console.error('Failed to unlock audio:', err);
      });
    },
    
    playBirthSound() {
      if (!this.soundEnabled) return;
      
      try {
        // Use a different variation for addition orders (sons)
        const audio = new Audio('/SoundNoti.mp3');
        // Removed playbackRate modification to prevent monitor interference
        audio.volume = 0.7; // Slightly louder for additions to differentiate
        audio.play().catch(error => {
          console.error('Error playing birth sound:', error);
          // Fallback to original sound
          const fallbackAudio = new Audio('/KitchenSoundeffect.mp3');
          fallbackAudio.playbackRate = 1.2;
          fallbackAudio.volume = 0.7;
          fallbackAudio.play().catch(fallbackError => 
            console.error('Error playing fallback birth sound:', fallbackError)
          );
        });
      } catch (error) {
        console.error('Error creating birth audio object:', error);
      }
    },
    
    playAdminMessageSound() {
      if (!this.soundEnabled) return;

      try {
        // Play sound for admin messages
        const audio = new Audio('/SoundNoti.mp3');
        // Removed playbackRate modification to prevent monitor interference
        audio.volume = 0.9; // Louder for important admin messages
        audio.play().catch(error => {
          console.error('Error playing admin message sound:', error);
        });
      } catch (error) {
        console.error('Error creating admin audio object:', error);
      }
    },

    playUberEatsSound() {
      try {
        // Stop any existing Uber sound
        this.stopUberEatsSound();

        // Play Uber Eats specific sound for new orders - LOOPS until accepted/denied
        const audio = new Audio('/UberEatsOrderSoundEffect.mp3');
        audio.volume = 0.9;
        audio.loop = true; // Loop until they react
        this.uberEatsAudio = audio; // Store reference to stop later

        audio.play().catch(error => {
          console.error('Error playing Uber Eats sound:', error);
          // Fallback to regular notification sound (also looping)
          const fallbackAudio = new Audio('/SoundNoti.mp3');
          fallbackAudio.volume = 0.9;
          fallbackAudio.loop = true;
          this.uberEatsAudio = fallbackAudio;
          fallbackAudio.play().catch(() => {});
        });
      } catch (error) {
        console.error('Error creating Uber Eats audio:', error);
      }
    },

    stopUberEatsSound() {
      if (this.uberEatsAudio) {
        this.uberEatsAudio.pause();
        this.uberEatsAudio.currentTime = 0;
        this.uberEatsAudio = null;
      }
    },

    async loadCompletedOrders() {
      console.log('🕐 Starting archive load...');
      console.log('Restaurant ID:', this.selectedRestaurantId);
      console.log('Selected Station:', this.selectedStation);
      
      try {
        // Use Firestore completed_orders collection (matches OrderArchiveWatcher pattern)
        await this.loadFromFirestoreCompletedOrders();
        
      } catch (error) {
        console.error('❌ Error loading completed orders from Firestore, trying fallback:', error);
        try {
          await this.loadFromRealtimeDatabase();
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          this.errorState.ordersFetch = true;
        }
      }
    },
    
    async loadFromFirestoreCompletedOrders() {
      if (!this.selectedRestaurantId || !this.selectedStation?.destinationCode) {
        console.warn('Cannot load archive: missing restaurant ID or station');
        this.completedOrders = {};
        return;
      }
      
      try {
        console.log('🗄️ Loading archive orders from Firestore completed_orders collection...');
        
        // Calculate 6 hours ago timestamp
        const sixHoursAgo = new Date();
        sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
        const sixHoursAgoISO = sixHoursAgo.toISOString();
        
        console.log('🕐 Looking for orders archived after:', sixHoursAgoISO);
        
        // Import Firestore functions
        const { getFirestore, collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
        const firestore = getFirestore();
        
        // Query Firestore completed_orders collection (matches OrderArchiveWatcher pattern)
        const completedOrdersRef = collection(firestore, 'restaurants', this.selectedRestaurantId, 'completed_orders');
        const archiveQuery = query(
          completedOrdersRef,
          where('archivedAt', '>=', sixHoursAgoISO),
          orderBy('archivedAt', 'desc'),
          limit(100) // Increased limit for better coverage
        );
        
        const querySnapshot = await getDocs(archiveQuery);
        
        if (!querySnapshot.empty) {
          const archiveOrders = {};
          let stationFilteredCount = 0;
          let totalCount = 0;
          
          querySnapshot.forEach((doc) => {
            totalCount++;
            const orderData = doc.data();
            const orderId = doc.id;
            
            // Check if this order has items for the current station
            const stationSection = orderData[this.selectedStation.destinationCode];
            const hasStationItems = stationSection && stationSection.items && stationSection.items.length > 0;
            
            if (hasStationItems) {
              stationFilteredCount++;
              // Transform the data to match the expected format for ArchiveModal
              archiveOrders[orderId] = {
                ...orderData,
                id: orderId,
                // Use archivedAt timestamp for sorting (matches OrderArchiveWatcher format)
                completedAt: orderData.archivedAt ? new Date(orderData.archivedAt).getTime() : Date.now(),
                timestamp: orderData.timestamp || orderData.archivedAt
              };
            }
          });
          
          console.log(`✅ Loaded ${totalCount} total archive orders, ${stationFilteredCount} for station ${this.selectedStation.destinationCode}`);
          this.completedOrders = archiveOrders;
        } else {
          console.log('📭 No archive orders found in Firestore completed_orders collection');
          this.completedOrders = {};
        }
        
      } catch (error) {
        console.error('❌ Error loading archive from Firestore completed_orders collection:', error);
        throw error; // Re-throw to trigger fallback
      }
    },
    
    async loadFromFirestore() {
      // Import Firestore functions
      const { getFirestore, collection, query, where, orderBy, limit, onSnapshot, Timestamp } = await import('firebase/firestore');
      const db = getFirestore();
      
      // Calculate 6 hours ago timestamp
      const sixHoursAgo = new Date();
      sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
      const sixHoursAgoTimestamp = Timestamp.fromDate(sixHoursAgo);
      
      console.log('🕐 Loading from Firestore - last 6 hours since:', sixHoursAgo.toLocaleString());
      
      // Get current station
      const station = this.selectedStation?.destinationCode;
      if (!station) {
        console.warn('No station selected for archive');
        return;
      }
      
      // Create Firestore query for last 6 hours of completed orders
      const ordersRef = collection(db, 'restaurants', this.selectedRestaurantId, 'orders');
      const completedOrdersQuery = query(
        ordersRef,
        where(`${station}.status`, 'in', ['Completed', 'Ready']),
        where(`${station}.completedAt`, '>=', sixHoursAgoTimestamp),
        orderBy(`${station}.completedAt`, 'desc'),
        limit(100)
      );
      
      // Set up real-time listener
      this.completedOrdersListener = onSnapshot(completedOrdersQuery, (snapshot) => {
        const completed = {};
        
        console.log(`📦 Firestore returned ${snapshot.size} completed orders`);
        
        snapshot.forEach((doc) => {
          const order = doc.data();
          const orderId = doc.id;
          
          // Process main order if it's completed
          if (order[station] && (order[station].status === 'Completed' || order[station].status === 'Ready')) {
            const completedAt = order[station].completedAt;
            
            if (completedAt && completedAt.toDate() >= sixHoursAgo) {
              const processedOrder = {
                ...order,
                id: orderId,
                items: order[station]?.items || [],
                completedAt: completedAt.toDate().getTime(),
                status: order[station].status,
                station: station
              };
              completed[orderId] = processedOrder;
            }
          }
        });
        
        console.log(`✅ Processed ${Object.keys(completed).length} completed orders`);
        this.completedOrders = completed;
      });
    },
    
    async loadFromRealtimeDatabase() {
      // Import Realtime Database functions
      const { ref, query, orderByChild, limitToLast, onValue } = await import('firebase/database');
      const db = this.$firebase.database();
      
      console.log('🕐 Loading from Realtime Database...');
      
      // Calculate 6 hours ago timestamp
      const sixHoursAgo = Date.now() - (6 * 60 * 60 * 1000);
      console.log('Six hours ago timestamp:', sixHoursAgo, new Date(sixHoursAgo).toLocaleString());
      
      // Get current station
      const station = this.selectedStation?.destinationCode;
      console.log('Station code:', station);
      
      // Query the orders collection
      const ordersRef = ref(db, `restaurants/${this.selectedRestaurantId}/orders`);
      const completedQuery = query(ordersRef, orderByChild('timestamp'), limitToLast(200));
      
      this.completedOrdersListener = onValue(completedQuery, (snapshot) => {
        const orders = snapshot.val() || {};
        const completed = {};
        
        console.log(`📦 Realtime DB returned ${Object.keys(orders).length} total orders`);
        
        Object.entries(orders).forEach(([orderId, order]) => {
          if (!order) return;
          
          // Check main order completion
          if (station && order[station]) {
            const sectionStatus = order[station].status;
            const completedAt = order[station].completedAt || order.timestamp || 0;
            
            // Check if completed and within last 6 hours
            if ((sectionStatus === 'Completed' || sectionStatus === 'Ready') && completedAt >= sixHoursAgo) {
              console.log(`✅ Found completed order ${orderId}:`, sectionStatus, new Date(completedAt).toLocaleString());
              
              completed[orderId] = {
                ...order,
                id: orderId,
                items: order[station]?.items || [],
                completedAt: completedAt,
                status: sectionStatus,
                station: station
              };
            }
          }
          
          // Check additions
          if (order.additions) {
            Object.entries(order.additions).forEach(([additionId, addition]) => {
              if (station && addition[station]) {
                const additionStatus = addition[station].status;
                const additionCompletedAt = addition[station].completedAt || addition.timestamp || 0;
                
                if ((additionStatus === 'Completed' || additionStatus === 'Ready') && additionCompletedAt >= sixHoursAgo) {
                  const virtualOrderId = `${orderId}_${additionId}`;
                  console.log(`✅ Found completed addition ${virtualOrderId}:`, additionStatus);
                  
                  completed[virtualOrderId] = {
                    ...order,
                    id: virtualOrderId,
                    parentOrderId: orderId,
                    items: addition[station]?.items || [],
                    completedAt: additionCompletedAt,
                    status: additionStatus,
                    isAddition: true,
                    waiter: addition.addedBy || order.waiter,
                    station: station
                  };
                }
              }
            });
          }
        });
        
        console.log(`✅ Final completed orders count: ${Object.keys(completed).length}`);
        console.log('Completed orders:', completed);
        this.completedOrders = completed;
      });
    },
    
    async clearArchive() {
      try {
        const { getFirestore, collection, query, where, getDocs, deleteDoc, orderBy } = await import('firebase/firestore');
        const firestore = getFirestore();
        const station = this.selectedStation?.destinationCode;
        
        if (!station) {
          alert('لا يوجد محطة محددة لمسح الأرشيف');
          return;
        }
        
        // Calculate 6 hours ago
        const sixHoursAgo = new Date();
        sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
        const sixHoursAgoISO = sixHoursAgo.toISOString();
        
        // Confirm with user
        const confirmClear = confirm(`هل تريد مسح جميع الطلبات المكتملة في محطة ${station} من آخر 6 ساعات؟\n\nهذا العمل لا يمكن التراجع عنه!`);
        if (!confirmClear) return;
        
        console.log('🗑️ Clearing archive for station:', station, 'from last 6 hours');
        
        // Query completed_orders collection (matches OrderArchiveWatcher pattern)
        const completedOrdersRef = collection(firestore, 'restaurants', this.selectedRestaurantId, 'completed_orders');
        const archiveQuery = query(
          completedOrdersRef,
          where('archivedAt', '>=', sixHoursAgoISO),
          orderBy('archivedAt', 'desc')
        );
        
        const snapshot = await getDocs(archiveQuery);
        
        if (snapshot.empty) {
          alert(this.$t('noCompletedOrdersToClear'));
          return;
        }
        
        // Filter and delete orders that have items for the current station
        let processedCount = 0;
        const deletePromises = [];
        
        snapshot.forEach((doc) => {
          const orderData = doc.data();
          const stationSection = orderData[station];
          const hasStationItems = stationSection && stationSection.items && stationSection.items.length > 0;
          
          if (hasStationItems) {
            deletePromises.push(deleteDoc(doc.ref));
            processedCount++;
          }
        });
        
        // Execute all deletions
        await Promise.all(deletePromises);
        
        // Clear local display
        this.completedOrders = {};
        
        console.log(`✅ Successfully cleared ${processedCount} completed orders from station ${station}`);
        alert(`تم مسح ${processedCount} طلبة مكتملة من محطة ${station} بنجاح!`);
        
      } catch (error) {
        console.error('❌ Error clearing archive:', error);
        alert('حدث خطأ أثناء مسح الأرشيف. حاول مرة أخرى.');
      }
    },
    
    getOrderFamilyColor(orderId) {
      // Check if we already have a color for this family
      if (this.orderFamilyColors[orderId]) {
        return this.orderFamilyColors[orderId];
      }
      
      // Generate a new family color with RGB values for shadows
      const colors = [
        { hex: '#3B82F6', rgb: '59, 130, 246' }, // blue
        { hex: '#10B981', rgb: '16, 185, 129' }, // green  
        { hex: '#F59E0B', rgb: '245, 158, 11' }, // yellow
        { hex: '#EF4444', rgb: '239, 68, 68' }, // red
        { hex: '#8B5CF6', rgb: '139, 92, 246' }, // purple
        { hex: '#EC4899', rgb: '236, 72, 153' }, // pink
        { hex: '#14B8A6', rgb: '20, 184, 166' }, // teal
        { hex: '#F97316', rgb: '249, 115, 22' }, // orange
      ];
      
      // Simple hash to consistently pick a color based on order ID
      let hash = 0;
      for (let i = 0; i < orderId.length; i++) {
        hash = orderId.charCodeAt(i) + ((hash << 5) - hash);
      }
      const colorIndex = Math.abs(hash) % colors.length;
      const familyColor = colors[colorIndex].hex;
      
      // Store the color for this family
      this.orderFamilyColors[orderId] = familyColor;
      return familyColor;
    },
    
    updateOrderFamilyStatus(orderId) {
      // When an order gets children, update its family color
      const order = this.orders[orderId];
      if (order && order._hasChildren && !order._familyColor) {
        Vue.set(this.orders[orderId], '_familyColor', this.getOrderFamilyColor(orderId));
      }
    },
    
    createMergedOrder(parentOrder, childrenOrders) {
      const currentStation = this.selectedStation?.destinationCode;
      if (!currentStation) return parentOrder;
      
      // Start with the parent order as base
      const mergedOrder = {
        ...parentOrder,
        isMergedFamily: true,
        childOrders: childrenOrders,
        mergedWith: childrenOrders.map(child => child.id)
      };
      
      // Combine items from parent and all children
      const parentItems = parentOrder[currentStation]?.items || [];
      let allItems = [...parentItems];
      
      // Add items from each child
      childrenOrders.forEach(child => {
        const childItems = child[currentStation]?.items || [];
        allItems = allItems.concat(childItems.map(item => ({
          ...item,
          isFromAddition: true,
          additionOrderId: child.id
        })));
      });
      
      // Update the merged order's station section with combined items
      mergedOrder[currentStation] = {
        ...parentOrder[currentStation],
        items: allItems
      };
      
      return mergedOrder;
    },
    
    
    async handleCriticalAlert(order) {
      console.log('🚨 Critical alert triggered for order:', order.id);

      // Only show alert if not already acknowledged
      if (!this.acknowledgedCriticalOrders.has(order.id)) {
        this.criticalOrder = order;
        this.showCriticalAlert = true;
        this.playCriticalSound();
      } else {
        console.log('⏭️ Critical alert already acknowledged for order:', order.id);
      }
    },
    
    async handleCriticalPrepare(orderId) {
      // Stop the repeating sound
      this.stopCriticalSound();

      this.startPreparingOrder(orderId);
      this.showCriticalAlert = false;
      this.criticalOrder = null;
    },
    
    async handleCriticalAcknowledge(orderId) {
      // Stop the repeating sound
      this.stopCriticalSound();

      // Mark as acknowledged so it won't show again
      this.acknowledgedCriticalOrders.add(orderId);
      this.showCriticalAlert = false;
      this.criticalOrder = null;
    },
    
    closeCriticalAlert() {
      // Stop the repeating sound
      this.stopCriticalSound();
      
      this.showCriticalAlert = false;
      this.criticalOrder = null;
    },
    
    playCriticalSound() {
      if (!this.soundEnabled) return;
      
      // Clear any existing interval
      if (this.criticalSoundInterval) {
        clearInterval(this.criticalSoundInterval);
      }
      
      const playSound = () => {
        try {
          // Stop any currently playing audio first
          if (this.currentCriticalAudio) {
            this.currentCriticalAudio.pause();
            this.currentCriticalAudio.currentTime = 0;
          }
          
          // Play the new 1-hour warning sound
          const audio = new Audio('/1Hourwarning.mp3');
          audio.volume = 0.9; // High volume for critical alerts
          this.currentCriticalAudio = audio; // Track the current audio
          
          audio.play().catch(error => {
            console.error('Error playing 1-hour warning sound:', error);
            // Fallback to original sound if the new one fails
            const fallbackAudio = new Audio('/SoundNoti.mp3');
            fallbackAudio.playbackRate = 0.7;
            fallbackAudio.volume = 1.0;
            this.currentCriticalAudio = fallbackAudio; // Track fallback audio
            fallbackAudio.play().catch(fallbackError => {
              console.error('Error playing fallback critical sound:', fallbackError);
            });
          });
        } catch (error) {
          console.error('Error creating critical audio object:', error);
          // Fallback to original sound
          try {
            const fallbackAudio = new Audio('/SoundNoti.mp3');
            fallbackAudio.playbackRate = 0.7;
            fallbackAudio.volume = 1.0;
            this.currentCriticalAudio = fallbackAudio; // Track fallback audio
            fallbackAudio.play();
          } catch (fallbackError) {
            console.error('Error with fallback sound:', fallbackError);
          }
        }
      };
      
      // Play immediately
      playSound();
      
      // Then repeat every 5 seconds until user responds
      this.criticalSoundInterval = setInterval(() => {
        if (this.showCriticalAlert) {
          playSound();
        } else {
          // Stop playing if alert is no longer shown
          clearInterval(this.criticalSoundInterval);
          this.criticalSoundInterval = null;
        }
      }, 5000); // Repeat every 5 seconds
    },
    
    stopCriticalSound() {
      console.log('🔇 EMERGENCY STOP: Stopping all critical sounds immediately...');
      
      // Stop the interval
      if (this.criticalSoundInterval) {
        clearInterval(this.criticalSoundInterval);
        this.criticalSoundInterval = null;
        console.log('✅ Sound interval cleared');
      }
      
      // Stop the currently playing audio immediately
      if (this.currentCriticalAudio) {
        this.currentCriticalAudio.pause();
        this.currentCriticalAudio.currentTime = 0;
        this.currentCriticalAudio.volume = 0; // Mute it too
        this.currentCriticalAudio = null;
        console.log('✅ Current audio stopped immediately');
      }
      
      // EMERGENCY: Stop ALL audio elements on the page
      const allAudio = document.querySelectorAll('audio');
      allAudio.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
      });
      
      // Stop any Audio objects that might be playing
      try {
        // Create a silent audio to override any playing sounds
        const silentAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt559NEAxQp+PwtmMcBjiR1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAfBT2a2/DBbykGJnzJ8NqLOAgfaLvt');
        silentAudio.volume = 0;
        silentAudio.play();
      } catch (e) {
        // Ignore errors
      }
      
      console.log('🔇 ALL AUDIO STOPPED - Emergency stop complete');
    },
    
    async logCriticalAlert(order) {
      try {
        console.log('🔄 Starting Firebase logging process...');
        console.log('📋 Restaurant ID:', this.selectedRestaurantId);
        console.log('📋 Order data:', order);
        
        const { ref, push } = await import('firebase/database');
        const db = this.$firebase.database();
        
        console.log('✅ Firebase imports successful');
        console.log('✅ Database connection:', db ? 'Connected' : 'Failed');
        
        // Create log entry
        const logData = {
          timestamp: Date.now(),
          orderId: order.id,
          tableNumber: order.tableNumber,
          orderNumber: order.orderNumber,
          waiter: order.waiter?.name || order.waiter || 'Unknown',
          station: this.selectedStation?.destinationCode || 'Unknown',
          stationName: this.selectedStation?.name || 'Unknown',
          restaurantId: this.selectedRestaurantId,
          orderCreatedAt: order.timestamp,
          elapsedMinutes: this.calculateElapsedMinutes(order.timestamp),
          alertType: 'critical_1_hour',
          orderType: order.orderType || 'dine-in',
          totalItems: this.getTotalItemsCount(order),
          criticalItems: this.getCriticalItems(order), // NEW: Add the items
          deviceInfo: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        };
        
        console.log('📝 Log data prepared:', logData);
        
        // Log to Firebase under the specific order
        const orderPath = `restaurants/${this.selectedRestaurantId}/orders/${order.id}/critical_alerts`;
        console.log('📍 Firebase path:', orderPath);
        
        const orderAlertsRef = ref(db, orderPath);
        console.log('📍 Firebase ref created');
        
        const result = await push(orderAlertsRef, logData);
        console.log('✅ Successfully logged to Firebase!', result.key);
        console.log('🎯 Critical alert logged to Firebase:', logData);
        
      } catch (error) {
        console.error('❌ Error logging critical alert to Firebase:', error);
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
    },
    
    calculateElapsedMinutes(timestamp) {
      if (!timestamp) return 0;
      
      const orderTime = typeof timestamp === 'string' 
        ? new Date(timestamp).getTime()
        : parseInt(timestamp);
      
      const elapsed = Date.now() - orderTime;
      return Math.floor(elapsed / 60000);
    },
    
    getTotalItemsCount(order) {
      let totalItems = 0;
      const currentStation = this.selectedStation?.destinationCode;
      
      if (currentStation && order[currentStation]?.items) {
        totalItems = order[currentStation].items.reduce((sum, item) => {
          return sum + (parseInt(item.quantity) || 1);
        }, 0);
      } else {
        // Count items from all stations if no specific station
        ['K', 'D', 'O'].forEach(section => {
          if (order[section]?.items) {
            totalItems += order[section].items.reduce((sum, item) => {
              return sum + (parseInt(item.quantity) || 1);
            }, 0);
          }
        });
      }
      
      return totalItems;
    },
    
    getCriticalItems(order) {
      const currentStation = this.selectedStation?.destinationCode;
      let criticalItems = [];
      
      if (currentStation && order[currentStation]?.items) {
        // Get items from current station only
        criticalItems = order[currentStation].items.map(item => ({
          articleNumber: item.articleNumber,
          name: item.name || 'Unknown Item',
          name_ar: item.name_ar || item.name || 'Unknown Item',
          quantity: item.quantity || 1,
          notes: item.notes || '',
          destination: item.destination || currentStation,
          cancelled: item.cancelled || false,
          addedAt: item.addedAt || null,
          isFromAddition: item.isFromAddition || false
        }));
      } else {
        // Get items from all stations if no specific station
        ['K', 'D', 'O'].forEach(section => {
          if (order[section]?.items) {
            const sectionItems = order[section].items.map(item => ({
              articleNumber: item.articleNumber,
              name: item.name || 'Unknown Item',
              name_ar: item.name_ar || item.name || 'Unknown Item',
              quantity: item.quantity || 1,
              notes: item.notes || '',
              destination: item.destination || section,
              cancelled: item.cancelled || false,
              addedAt: item.addedAt || null,
              isFromAddition: item.isFromAddition || false,
              station: section // Add which station this item belongs to
            }));
            criticalItems = criticalItems.concat(sectionItems);
          }
        });
      }
      
      // Filter out cancelled items
      return criticalItems.filter(item => !item.cancelled);
    },
    
    async logCriticalAlertAction(order, action) {
      try {
        const { ref, push } = await import('firebase/database');
        const db = this.$firebase.database();
        
        // Create action log entry
        const actionLogData = {
          timestamp: Date.now(),
          orderId: order.id,
          tableNumber: order.tableNumber,
          action: action, // 'prepare' or 'acknowledge'
          alertType: 'critical_1_hour_response',
          station: this.selectedStation?.destinationCode || 'Unknown',
          stationName: this.selectedStation?.name || 'Unknown',
          restaurantId: this.selectedRestaurantId,
          elapsedMinutes: this.calculateElapsedMinutes(order.timestamp),
          responseTime: new Date().toISOString()
        };
        
        // Log to Firebase under the specific order
        const orderResponsesRef = ref(db, `restaurants/${this.selectedRestaurantId}/orders/${order.id}/critical_alert_responses`);
        await push(orderResponsesRef, actionLogData);

        console.log(`Critical alert ${action} logged to Firebase:`, actionLogData);
      } catch (error) {
        console.error(`Error logging critical alert ${action} to Firebase:`, error);
      }
    },

    // Feedback modal methods
    startFeedbackTimer() {
      // Check immediately on mount
      this.checkFeedbackTime();

      // Then check every minute
      this.feedbackCheckTimer = setInterval(() => {
        this.checkFeedbackTime();
      }, 60000); // Check every 60 seconds
    },

    checkFeedbackTime() {
      const now = new Date();

      // Convert to Berlin time (Europe/Berlin timezone)
      const berlinTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
      const hours = berlinTime.getHours();
      const minutes = berlinTime.getMinutes();

      // Check if it's 23:10 Berlin time (between 23:10 and 23:11)
      if (hours === 23 && minutes === 10) {
        this.checkAndShowFeedback();
      }
    },

    checkAndShowFeedback() {
      const lastSubmittedDate = localStorage.getItem('feedbackSubmittedDate');
      const today = new Date().toDateString();

      // Only show if not submitted today
      if (lastSubmittedDate !== today) {
        this.showFeedbackModal = true;
      }
    },

    closeFeedbackModal() {
      this.showFeedbackModal = false;
    },

    async checkRestaurantStatus() {
      if (!this.selectedRestaurantId) return;

      try {
        const { ref, onValue } = await import('firebase/database');
        const db = this.$firebase.database();
        const statusRef = ref(db, `restaurants/${this.selectedRestaurantId}/info`);

        // Set up real-time listener
        this.statusListener = onValue(statusRef, (snapshot) => {
          const info = snapshot.val();
          console.log('📊 Restaurant status updated:', info);

          if (info?.status === 'suspended') {
            console.log('🔒 Suspended status detected - showing lockout screen');
            this.isSystemSuspended = true;
            this.showSystemWarning = false;
          } else if (info?.status === 'warning' && info?.warningData?.shutdownDate) {
            console.log('⚠️ Warning status detected');
            this.warningShutdownDate = info.warningData.shutdownDate;
            this.customWarningMessage = info.warningData.customWarningMessage || 'Zahlung fällig';

            // Check if user already dismissed the warning
            const hasBeenDismissed = localStorage.getItem('systemWarningDismissed') === 'true';
            if (!hasBeenDismissed) {
              console.log('📢 Showing warning modal (not dismissed yet)');
              this.showSystemWarning = true;
            } else {
              console.log('✅ Warning already dismissed by user');
              this.showSystemWarning = false;
            }
            this.isSystemSuspended = false;
          } else {
            console.log('✅ Status is active');
            this.isSystemSuspended = false;
            this.showSystemWarning = false;
          }
        }, (error) => {
          console.error('❌ Error in status listener:', error);
        });
      } catch (error) {
        console.error('Error setting up restaurant status listener:', error);
      }
    },

    closeSystemWarning() {
      this.showSystemWarning = false;
    },

    checkOwnershipNotice() {
      // Disabled - ownership notice no longer needed
      return;
    },

    handleOwnershipNoticeClose() {
      this.showOwnershipNotice = false;
      localStorage.setItem('orderDisplayOwnershipNoticeSeen', 'true');
    },

    handleMealWaveDisconnected() {
      // Stop listening for MealWave orders
      if (this.mealwaveOrdersListener) {
        this.mealwaveOrdersListener();
        this.mealwaveOrdersListener = null;
      }
      this.mealwaveOrders = {};
      this.connectedMealWaveRestaurantId = null;
      this.mealwaveConnectionStatus = null;
      console.log('📱 MealWave disconnected, stopped listening for orders');
    }
  }
};
</script>

<style scoped>
/* Scale entire interface to 75% */
.scaled-interface {
  transform: scale(0.75);
  transform-origin: top left;
  width: 133.33%; /* Compensate for scale to fill viewport */
  height: 133.33%; /* Compensate for scale to fill viewport */
}

/* Top Edge Touch Zone */
.top-touch-zone {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 1001;
  cursor: pointer;
  /* Invisible but touchable */
  background: transparent;
  /* Touch optimization */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  /* For debugging - uncomment to see touch zone */
  /* background: rgba(255, 0, 0, 0.1); */
}

.top-touch-zone:active {
  background: rgba(59, 130, 246, 0.1);
}

/* Enhanced Header Container */
.header-container.enhanced-header {
  background: linear-gradient(135deg, rgba(17, 24, 33, 0.98), rgba(31, 41, 55, 0.98));
  backdrop-filter: none;
  border-bottom: 2px solid rgba(59, 130, 246, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 1100;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
}

/* Clean AppHeader Wrapper */
.clean-app-header {
  position: relative;
}

/* Bell Ringing Animation */
.bell-ring {
  animation: bell-ring 2s ease-in-out infinite;
  transform-origin: top center;
}

@keyframes bell-ring {
  0%, 100% { transform: rotate(0); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-12deg); }
  30% { transform: rotate(10deg); }
  40% { transform: rotate(-8deg); }
  50% { transform: rotate(0); }
}

/* Fire Flicker Animation */
.fire-flicker {
  animation: fire-flicker 1.5s ease-in-out infinite;
}

@keyframes fire-flicker {
  0%, 100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  25% {
    transform: scale(1.1) translateY(-1px);
    opacity: 0.9;
  }
  50% {
    transform: scale(0.95) translateY(1px);
    opacity: 1;
  }
  75% {
    transform: scale(1.05) translateY(-1px);
    opacity: 0.85;
  }
}

/* Count Badge - Clean circular style */
.count-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  font-size: 16px;
  font-weight: 800;
  border-radius: var(--ods-radius-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  line-height: 1;
  text-align: center;
}

.count-badge.blue {
  background: var(--ods-surface);
  color: var(--ods-accent-ink);
}

.count-badge.yellow {
  background: var(--ods-surface);
  color: #f5b942;
}

/* Just make sure buttons are touch-friendly */
.clean-app-header >>> button {
  min-height: 44px !important;
  min-width: 44px !important;
  touch-action: manipulation !important;
  transition: all 0.1s ease !important;
}

.clean-app-header >>> button:active {
  transform: scale(0.95) !important;
}



/* Header slide animations */
.header-slide-enter-active, .header-slide-leave-active {
  transition: all 0.3s ease-out;
}

.header-slide-enter, .header-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.header-slide-enter-to, .header-slide-leave {
  transform: translateY(0);
  opacity: 1;
}

/* Section Divider - Simple and visible */
.section-divider {
  position: absolute;
  top: 0;
  left: 50%;
  bottom: 0;
  width: 1px;
  background: var(--ods-line-2);
  z-index: 10;
  transform: translateX(-0.5px);
}

/* Full height layout when header is hidden */
.full-height {
  height: 100vh !important;
}

/* Archive Modal - Clean Design */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  border-radius: var(--ods-radius);
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

/* Dark theme styles */
.modal-container.dark-theme {
  background: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid var(--ods-line);
}

/* Light theme styles */
.modal-container.light-theme {
  background: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
}

/* Dark theme header */
.dark-theme .modal-header {
  border-bottom: 1px solid var(--ods-line);
  background: linear-gradient(135deg, rgba(17, 24, 33, 0.98), rgba(31, 41, 55, 0.98));
}

/* Light theme header */
.light-theme .modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.98));
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

/* Dark theme close button */
.dark-theme .modal-close-btn {
  color: var(--ods-ink-2);
}

.dark-theme .modal-close-btn:hover {
  background: var(--ods-surface-3);
  color: var(--ods-ink);
}

/* Light theme close button */
.light-theme .modal-close-btn {
  color: var(--ods-ink-3);
}

.light-theme .modal-close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--ods-line);
}

.modal-body {
  padding: 20px;
  max-height: calc(90vh - 100px);
  overflow-y: auto;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.archive-card {
  /* Exact same styling as OrderCard */
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: var(--ods-radius);
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
  position: relative;
  padding: 16px;
  /* Yellow theme for completed */
  border-color: #FACC15;
}

/* Dark theme archive cards */
.dark-theme .archive-card {
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid var(--ods-line);
  border-color: #FACC15;
}

/* Light theme archive cards */
.light-theme .archive-card {
  background-color: var(--ods-surface);
  color: var(--ods-ink);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-color: #FACC15;
}

/* Dark theme archive card hover */
.dark-theme .archive-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.15);
}

/* Light theme archive card hover */
.light-theme .archive-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.archive-card::after {
  content: 'مكتمل';
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #D97706;
  color: var(--ods-ink);
  padding: 4px 12px;
  border-radius: var(--ods-radius);
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.archive-card .order-item {
  font-weight: 500;
}

/* Admin message styles */
#admin-message-container {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Birth animation for new addition orders */
.birth-animation-enter-active {
  transition: all 0.5s ease-out;
}

.birth-animation-enter {
  transform: translateY(-20px) scale(0.9);
  opacity: 0;
}

/* Special slide animation when baby appears next to mom */
.birth-animation-enter-active.birth-next-to-mom {
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.birth-animation-enter.birth-next-to-mom {
  transform: translateX(-30px) scale(0.8);
  opacity: 0;
}

/* Immediate leave animation (no delay) */
.birth-animation-leave-active {
  transition: all 0s;
  opacity: 0;
}

.birth-animation-leave-to {
  opacity: 0;
}

/* Parent shake animation before birth */
@keyframes parentShake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
  90% { transform: translateX(-1px); }
}

/* Parent glow animation */
@keyframes parentGlow {
  0% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.7); }
  50% { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0); }
  100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
}

.parent-shaking {
  animation: parentShake 0.5s ease-in-out;
}

.parent-glowing {
  animation: parentGlow 1s ease-out;
}

/* Attention animation for cards that moved from New to Preparing */
:deep(.attention-animation) {
  animation: attention-pulse 1s ease-out forwards !important;
  z-index: 100;
}

@keyframes attention-pulse {
  0% {
    background-color: transparent;
    box-shadow: inset 0 0 0 rgba(251, 191, 36, 0);
  }
  30% {
    background-color: rgba(251, 191, 36, 0.15);
    box-shadow: inset 0 0 20px rgba(251, 191, 36, 0.3);
    z-index: 100;
  }
  70% {
    background-color: rgba(251, 191, 36, 0.15);
    box-shadow: inset 0 0 20px rgba(251, 191, 36, 0.3);
    z-index: 100;
  }
  100% {
    background-color: transparent;
    box-shadow: inset 0 0 0 rgba(251, 191, 36, 0);
  }
}

/* Audio Blocked Indicator */
.audio-blocked-indicator {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: var(--ods-ink);
  padding: 12px 24px;
  border-radius: var(--ods-radius-lg);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(245, 158, 11, 0.4);
  animation: pulse-sound 2s ease-in-out infinite;
}

.audio-blocked-indicator i {
  font-size: 18px;
}

.audio-blocked-indicator:hover {
  transform: translateX(-50%) scale(1.05);
  box-shadow: 0 6px 25px rgba(245, 158, 11, 0.5);
}

@keyframes pulse-sound {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(245, 158, 11, 0.4);
  }
  50% {
    box-shadow: 0 4px 30px rgba(245, 158, 11, 0.6);
  }
}
</style>

<style>
/* Column scaffold — shared with the dev preview */
.ods-column { background: var(--ods-bg-2); }
.ods-column + .ods-column { border-left: 1px solid var(--ods-line); }
.ods-col-head { position: relative; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 12px 56px; color: #fff; font-weight: 800; border-bottom: 1px solid rgba(0,0,0,.12); }
.ods-col-head h2 { font-size: 17px; font-weight: 800; margin: 0; letter-spacing: .01em; }
.ods-col-head > i { position: absolute; left: 16px; font-size: 18px; opacity: .95; }
.ods-col-head .count-badge { position: absolute; right: 14px; display: flex; align-items: center; justify-content: center; min-width: 30px; height: 30px; padding: 0 10px; font-size: 15px; font-weight: 800; border-radius: var(--ods-radius); background: rgba(255,255,255,.92); color: var(--ods-new); line-height: 1; font-variant-numeric: tabular-nums; }
.ods-col-head--new { background: var(--ods-new); }
.ods-col-head--prep { background: var(--ods-prep); }
.ods-col-head--prep .count-badge { color: var(--ods-prep); }
.ods-empty { color: var(--ods-ink-3); }
.ods-admin-msg { background: var(--ods-prep-tint); border: 1px solid var(--ods-prep); color: var(--ods-ink); border-radius: var(--ods-radius); }
</style>